import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, IconButton,
  Breadcrumbs, Link, Collapse,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { axiosPartners } from "../../axios/axiosConfig";
import toast from "react-hot-toast";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const fmt = (val) =>
  val
    ? new Date(val).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

const statusColor = (s) => {
  const v = s?.toUpperCase() || "";
  if (v === "COMPLETED")   return { bg: "#dcfce7", color: "#16a34a" };
  if (v === "OPEN")        return { bg: "#dbeafe", color: "#2563eb" };
  if (v === "PENDING")     return { bg: "#fef9c3", color: "#b45309" };
  if (v === "CANCELLED" || v === "CANCEL") return { bg: "#fee2e2", color: "#dc2626" };
  if (v === "INACTIVE")    return { bg: "#f1f5f9", color: "#64748b" };
  if (v.includes("GREEN")) return { bg: "#dcfce7", color: "#16a34a" };
  if (v.includes("RED"))   return { bg: "#fee2e2", color: "#dc2626" };
  return { bg: "#f1f5f9", color: "#64748b" };
};

const signalColor = (s) =>
  s?.toLowerCase() === "buy"
    ? { bg: "#dcfce7", color: "#16a34a" }
    : { bg: "#fee2e2", color: "#dc2626" };

const TradeRow = ({ trade, index }) => {
  const [open, setOpen] = useState(false);
  const sc = statusColor(trade.order_status);
  const sg = signalColor(trade.entry_signal);
  const hasOrders = trade.orders?.length > 0;

  return (
    <>
      <TableRow
        onClick={() => hasOrders && setOpen((p) => !p)}
        sx={{ cursor: hasOrders ? "pointer" : "default", "&:hover": { background: "#f8fafc" }, transition: "background 0.15s" }}
      >
        <TableCell sx={{ fontSize: 12, color: "#94a3b8", py: 1.5, width: 36 }}>
          {hasOrders
            ? open
              ? <KeyboardArrowDownIcon sx={{ fontSize: 16, color: "#016551", verticalAlign: "middle" }} />
              : <KeyboardArrowRightIcon sx={{ fontSize: 16, color: "#94a3b8", verticalAlign: "middle" }} />
            : index + 1}
        </TableCell>

        <TableCell sx={{ py: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#04343a" }}>{trade.symbol_name}</Typography>
          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{trade.trading_symbol} · {trade.exchange_segment}</Typography>
        </TableCell>

        <TableCell sx={{ py: 1.5 }}>
          <Chip label={trade.entry_signal} size="small"
            sx={{ background: sg.bg, color: sg.color, fontWeight: 700, fontSize: 11, height: 22, borderRadius: "6px" }} />
        </TableCell>

        <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
          <Typography sx={{ fontSize: 12, color: "#0f172a" }}>
            Qty: <b>{trade.green_quantity ?? "—"}</b>
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#64748b" }}>
            Code: {trade.client_code || "—"}
          </Typography>
        </TableCell>

        <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
          <Typography sx={{ fontSize: 12 }}>
            <span style={{ color: "#16a34a", fontWeight: 700 }}>G </span>
            {trade.green_price != null ? `₹${trade.green_price}` : "—"}
          </Typography>
          <Typography sx={{ fontSize: 12 }}>
            <span style={{ color: "#dc2626", fontWeight: 700 }}>R </span>
            {trade.red_alert_price != null ? `₹${trade.red_alert_price}` : "—"}
          </Typography>
        </TableCell>

        <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
          <Typography sx={{ fontSize: 11 }}>
            <span style={{ color: "#16a34a", fontWeight: 600 }}>H</span> {trade.day_high ?? "—"}
          </Typography>
          <Typography sx={{ fontSize: 11 }}>
            <span style={{ color: "#dc2626", fontWeight: 600 }}>L</span> {trade.day_low ?? "—"}
          </Typography>
        </TableCell>

        <TableCell sx={{ py: 1.5 }}>
          <Chip label={trade.order_status || "—"} size="small"
            sx={{ background: sc.bg, color: sc.color, fontWeight: 700, fontSize: 10, height: 20, borderRadius: "4px" }} />
          {trade.exit_reason && (
            <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.3 }}>{trade.exit_reason}</Typography>
          )}
          {trade.cancel_reason && (
            <Typography sx={{ fontSize: 10, color: "#dc2626", mt: 0.3 }}>{trade.cancel_reason}</Typography>
          )}
        </TableCell>

        <TableCell sx={{ py: 1.5 }}>
          {hasOrders && (
            <Chip label={`${trade.orders.length} Order${trade.orders.length !== 1 ? "s" : ""}`} size="small"
              sx={{ background: "#eff6ff", color: "#2563eb", fontWeight: 700, fontSize: 11, height: 22, borderRadius: "6px" }} />
          )}
        </TableCell>

        <TableCell sx={{ fontSize: 11, color: "#94a3b8", py: 1.5, whiteSpace: "nowrap" }}>
          {fmt(trade.created_at)}
        </TableCell>
      </TableRow>

      {/* Nested orders */}
      {hasOrders && (
        <TableRow>
          <TableCell colSpan={9} sx={{ p: 0, borderBottom: open ? "1px solid #e2e8f0" : "none" }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ background: "#f8fafc", px: 4, py: 1.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#016551", mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Orders
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["Order ID", "Type", "Qty", "Product", "Order Type", "Exec. Price", "Status", "Created"].map((h) => (
                        <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, color: "#64748b", py: 1, whiteSpace: "nowrap" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trade.orders.map((o) => {
                      const oc = statusColor(o.order_status);
                      return (
                        <TableRow key={o.order_id} sx={{ "&:hover": { background: "#f0fdf4" } }}>
                          <TableCell sx={{ fontSize: 11, color: "#64748b", fontFamily: "monospace", py: 1 }}>{o.order_id}</TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <Chip
                              label={o.transaction_type === "B" ? "Buy" : "Sell"}
                              size="small"
                              sx={{
                                background: o.transaction_type === "B" ? "#dcfce7" : "#fee2e2",
                                color: o.transaction_type === "B" ? "#16a34a" : "#dc2626",
                                fontWeight: 700, fontSize: 10, height: 18, borderRadius: "4px",
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 11, color: "#0f172a", py: 1 }}>{o.quantity}</TableCell>
                          <TableCell sx={{ fontSize: 11, color: "#64748b", py: 1 }}>{o.product_code}</TableCell>
                          <TableCell sx={{ fontSize: 11, color: "#64748b", py: 1 }}>{o.order_type}</TableCell>
                          <TableCell sx={{ fontSize: 11, color: "#0f172a", py: 1 }}>
                            {o.execution_price != null ? `₹${o.execution_price}` : "—"}
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <Chip label={o.order_status} size="small"
                              sx={{ background: oc.bg, color: oc.color, fontWeight: 700, fontSize: 10, height: 18, borderRadius: "4px" }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: 10, color: "#94a3b8", py: 1, whiteSpace: "nowrap" }}>{fmt(o.created_at)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const SymbolUserTradeHistory = () => {
  const { symbolName, brokerName, clientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const decodedSymbol = decodeURIComponent(symbolName);
  const decodedBroker = decodeURIComponent(brokerName);

  const brokerId = location.state?.brokerId;
  const userId   = location.state?.clientId || clientId;
  const userName = location.state?.userName || clientId;

  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosPartners.get(
        `/brokers/trade-dashboard/?broker_id=${brokerId}&client_id=${userId}`
      );
      setTrades(res.data?.data ?? []);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to load trades");
    } finally {
      setLoading(false);
    }
  }, [brokerId, userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>

      {/* Header */}
      <Box sx={{ background: "#04343a", borderRadius: "12px", px: 3, py: 2, mb: 2, display: "flex", alignItems: "center", gap: 1.5, boxShadow: "0 4px 20px rgba(4,52,58,0.3)" }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: "#eef2f7", p: 0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <PersonIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
        <Box>
          <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
            {userName} — Trades
          </Typography>
          <Typography sx={{ color: "#b8d4c4", fontSize: 12 }}>
            {decodedSymbol} · {decodedBroker} · Click a trade to expand orders
          </Typography>
        </Box>
      </Box>

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" underline="hover" sx={{ fontSize: 12, color: "#016551", cursor: "pointer" }} onClick={() => navigate(-3)}>
          Symbolwise Dashboard
        </Link>
        <Link component="button" underline="hover" sx={{ fontSize: 12, color: "#016551", cursor: "pointer" }} onClick={() => navigate(-2)}>
          {decodedSymbol}
        </Link>
        <Link component="button" underline="hover" sx={{ fontSize: 12, color: "#016551", cursor: "pointer" }} onClick={() => navigate(-1)}>
          {decodedBroker}
        </Link>
        <Typography sx={{ fontSize: 12, color: "#0f172a", fontWeight: 600 }}>{userName}</Typography>
      </Breadcrumbs>

      {/* Table */}
      <Box sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <Box sx={{ background: "#04343a", px: 3, py: 1.5 }}>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
            {trades.length} Trade{trades.length !== 1 ? "s" : ""} — {userName}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#016551" }} size={36} />
          </Box>
        ) : trades.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No trades found</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["", "Symbol", "Signal", "Qty / Code", "Green / Red", "Day H/L", "Status", "Orders", "Created"].map((h) => (
                    <TableCell key={h} sx={{ background: "#04343a", color: "#fff", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", py: 1.5, whiteSpace: "nowrap" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {trades.map((trade, i) => (
                  <TradeRow key={trade.trade_id} trade={trade} index={i} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default SymbolUserTradeHistory;
